import { useEffect, useState, useRef } from 'react';
import { 
  PlayCircleOutlined, 
  PauseCircleOutlined, 
  InfoCircleOutlined, 
  EditOutlined, 
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { Button, Card, Tooltip, Drawer, Form, Input, Popconfirm, message } from 'antd';
import { AudioPlayerCardProps } from '../../../types/voice';
import styles from './index.module.scss';
import type { VoiceItem } from '../../../types/voice';
import { sqlInjectionPattern } from '../../../utils';
import { updateReferAudioName, deleteAudio } from '../../../api/voice';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';


const AudioPlayerCard: React.FC<AudioPlayerCardProps> = ({
  voice,
  currentPlayingId,
  onPlayStateChange,
  onUseButtonClick,
  onUpdateName,
  onDeleteAudio
}) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [hovered, setHovered] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  const [selectedAudio, setSelectedAudio] = useState<VoiceItem | null>(null)
  const [isNameEditing, setIsNameEditing] = useState<boolean>(false);
  const [editAudioName, setEditAudioName] = useState<string>('');
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false)
  const [isNameEditSubmitting, setIsNameEditSubmitting] = useState<boolean>(false)
  
  const { t } = useTranslation();


  useEffect(() => {
    // 只在明确的播放命令下才播放音频
    if (currentPlayingId === voice.id && !isPlaying && audioRef.current) {
      // 只有通过点击播放按钮(handlePlay)才会播放
      // 不会在组件加载时自动播放
    }

    // 如果当前不是选中的音频，但正在播放，则暂停并重置时间
    if (currentPlayingId !== voice.id && isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0; // 重置播放位置到开头
      setIsPlaying(false);
    }

    // 当音频不在播放状态时，也确保它的播放位置被重置
    if (!isPlaying && audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  }, [currentPlayingId, voice.id, isPlaying]);

  useEffect(() => {
    const onEnded = () => {
      setIsPlaying(false);
      onPlayStateChange(null);
    };

    audioRef.current?.addEventListener('ended', onEnded);
    return () => {
      audioRef.current?.removeEventListener('ended', onEnded);
    };
  }, [onPlayStateChange]);

  const handlePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0; // 暂停时重置到开头
      setIsPlaying(false);
      onPlayStateChange(null);
    } else {
      // 播放前先重置时间，确保从头开始播放
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.error("播放失败:", e));
      setIsPlaying(true);
      onPlayStateChange(voice.id);
    }
  };

  // 
  const handleAudioNameCancel = () => {
		setIsNameEditing(false);
	};

	const validateInputSafety = (input: string): boolean => {
		if (sqlInjectionPattern.test(input)) {
			message.error('输入内容不能包含特殊字符');
			return false;
		}

		// Additional validation rules
		if (input.length > 100) {
			message.error('名称长度不能超过100个字符');
			return false;
		}

		return true;
	};

	const handleAudioNameSave = async () => {
		if (!selectedAudio) return;

		const trimmedName = editAudioName.trim();

		if (!trimmedName) {
			message.error('视频名称不能为空');
			return;
		}

		if (!validateInputSafety(trimmedName)) {
			return;
		}

		try {
			setIsNameEditSubmitting(true);
			const videoId = selectedAudio.id
      
      const res = await updateReferAudioName({ 'id': videoId, 'name': trimmedName });

			if (res.data.status === 200) {
				message.success('视频名称更新成功');
				setIsNameEditing(false);
				
				var tmp = {...voice}
				tmp.name = trimmedName
				setSelectedAudio(tmp)
        if (onUpdateName) onUpdateName(tmp)

			} else {
				message.error('视频名称更新失败');
			}
      
		} catch (error) {
			// console.error('更新视频名称出错:', error);
			message.error('更新视频名称时发生错误');
		} finally {
			setIsNameEditSubmitting(false);
		}
	};

	const handleAudioNameEdit = () => {
		setEditAudioName(selectedAudio?.name || '');
		setIsNameEditing(true);
	};

	const handleDeleteAudio = async () => {
		// console.log("selectedVideo: ", selectedVideo)
		if (selectedAudio === null) {
			message.error("视频信息错误")
			return
		}
    try {
      setDeleteLoading(true)
		  const result = await deleteAudio({ 'id': selectedAudio.id })
      const {status} = result.data

      if (status === 200) {
        message.success("删除成功")
        if (onDeleteAudio) onDeleteAudio(selectedAudio.id)
        
        setDrawerOpen(false)
      }
    } catch (err) {
      message.error("删除失败")
    } finally {
      setDeleteLoading(false)
    }
    
    
	}

  const handleShowAudioDetails = () => {
    setSelectedAudio(voice)
    setDrawerOpen(true)

  }

  return (
    <>
      <Card
        className={styles.voiceCard}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <audio ref={audioRef} src={voice.audioUrl} />

        <div className={styles.cardContent}>
          <div className={styles.left}>
            {
              isPlaying ?
                <PauseCircleOutlined className={styles.play} onClick={handlePlay} /> :
                <PlayCircleOutlined className={styles.play} onClick={handlePlay} />
            }

            <div className={styles.title}>{voice.name}</div>
          </div>
          {hovered &&
            <div className={styles.voiceAction}>
              <Button
                size="small"
                className={styles.useButton}
                onClick={() => onUseButtonClick(voice)}
              >
                {t('apply')}
              </Button>
              {voice.public === 0 &&
                <Tooltip title={t('view_details')}>
                  <InfoCircleOutlined className={styles.infoIcon} onClick={handleShowAudioDetails} />
                </Tooltip>
              }
            </div>
          }
        </div>
      </Card>


      <Drawer
        title={t('audio_play_card.drawer.title')}
        placement="right"
        size="default"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
      >
        {selectedAudio && (
          <div className={styles.audioDetails}>

            <div className={styles.audioInfo}>
              <div className={styles.titleRow}>
                {isNameEditing ? (
                  <div className={styles.editNameContainer}>
                    <Form.Item
                      validateStatus={
                        sqlInjectionPattern.test(editAudioName) ? 'error' : ''
                      }
                      help={
                        sqlInjectionPattern.test(editAudioName)
                          ? t('form.help.sqlinject.tip')
                          : ''
                      }
                      className={styles.editFormItem}
                    >
                      <Input
                        value={editAudioName}
                        onChange={(e) => setEditAudioName(e.target.value)}
                        placeholder={t('form.edit.input_placehoder')}
                        maxLength={30}
                        className={styles.nameInput}
                        autoFocus
                      />
                    </Form.Item>
                    <div className={styles.editActions}>
                      <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        size="small"
                        onClick={handleAudioNameSave}
                        loading={isNameEditSubmitting}
                        className={styles.editButton}
                      />
                      <Button
                        icon={<CloseOutlined />}
                        size="small"
                        onClick={handleAudioNameCancel}
                        className={styles.editButton}
                      />
                    </div>
                  </div>
                ) : (
                  <div className={styles.titleWithEdit}>
                    <div className={styles.name}>{selectedAudio.name}</div>
                    <Tooltip title={t('edit_name')}>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={handleAudioNameEdit}
                        className={styles.editNameButton}
                      />
                    </Tooltip>
                    
                    <Popconfirm
                      title={t('popconfirm.title')}
                      placement="left"
                      description={t('popconfirm.description')}
                      onConfirm={handleDeleteAudio}
                     
                    >
                      <Tooltip title={t('popconfirm.voice.del_tip')}>

                        <Button variant="text" color="danger" icon={<DeleteOutlined />} className={styles.deleteButton} loading={deleteLoading} />
                      </Tooltip>
                    </Popconfirm>
                   
                  </div>
                )}


              </div>

              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>{t('audio_play_card.drawer.creation_time')}:</span>
                <span>{selectedAudio.createTime}</span>
              </div>

              <div className={styles.infoRow}> 
                <span className={styles.infoLabel}>{t('audio_play_card.drawer.text_lang')}:</span>  
                <span>{selectedAudio.language}</span>
              </div>

              <div className={styles.infoRow}> 
                <span className={styles.infoLabel}>{t('audio_play_card.drawer.text_content')}:</span>
                <span>{selectedAudio.audioText}</span>
              </div>
            
            </div>
          </div>
        )}

      </Drawer>
    </>
  );
};

export default AudioPlayerCard;